/**
 * The context overlay beside an invoice bill, so Greg is not approving it blind.
 * Mercedes + Greg, 2026-09-17: a $14,000 shrimp bill arrived with nothing on it but the
 * total, and box invoices arrived from a vendor we do not usually buy boxes from, with no
 * way to find out why short of emailing the buyer.
 *
 * Renders what lib/invoice-context.js computed and derives nothing of its own, so the
 * screen and the server can never disagree. The flags are questions, not verdicts: each
 * one names the number behind it.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, History, Users, TrendingUp, Check } from 'lucide-react';
import { apInvoiceContext, apVendorProfiles } from '../services/api';
import VendorProfileCard from './VendorProfileCard';

const money = (v) => (v == null || !isFinite(Number(v)) ? '—'
  : '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtDay = (d) => {
  if (!d) return '—';
  // A DATE string, read as a calendar day: new Date('2026-09-01') is UTC midnight and
  // prints as the day before in Eastern time.
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// "within the last hour" / "5 hours ago" / "3 days ago". Deliberately vague at the top
// end: the difference between 61 and 89 minutes does not change anybody's decision, and
// the difference between an hour and a week very much does.
const agoText = (hours) => {
  if (hours == null || !isFinite(Number(hours))) return 'at an unknown time';
  const h = Number(hours);
  if (h < 1.5) return 'within the last hour';
  if (h < 36) return `${Math.round(h)} hours ago`;
  return `${Math.round(h / 24)} days ago`;
};

const TONES = {
  bad: { bg: '#fdeaea', border: '#f3c2c2', fg: '#b42318' },
  warn: { bg: '#fff3e6', border: '#f6d4b8', fg: '#9a4a1c' },
  info: { bg: '#eef4fa', border: '#c9dcee', fg: 'var(--navy, #00395C)' },
};

const label = {
  fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px',
  color: 'var(--gray-500, #6b7280)', fontWeight: 600, marginBottom: 6,
  display: 'flex', alignItems: 'center', gap: 6,
};
const box = {
  border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 6, padding: 12,
};
const row = {
  display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13, padding: '2px 0',
};

function Stat({ k, v }) {
  return (
    <div style={row}>
      <span style={{ color: 'var(--gray-600, #4b5563)' }}>{k}</span>
      <span style={{ color: 'var(--navy, #00395C)', fontWeight: 600, textAlign: 'right' }}>{v}</span>
    </div>
  );
}

// ⚠️ DATES ARE SHOWN THE WAY PEOPLE WRITE THEM (Greg, 2026-10-09): "Aug 21, 2025", never
// "2025-08-21". The coach is told this in its prompt, but the tools hand it dates in the
// second form and it will sometimes echo one, and every conversation saved before the
// rule is full of them. So the screen rewrites any such date itself and does not depend
// on the model remembering. Only a real calendar date is touched; an invoice number that
// merely looks like one ("2025-13-40") is left alone.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function plainDates(s) {
  return String(s == null ? '' : s).replace(/\b(20\d{2})-(\d{2})-(\d{2})\b/g, (m, y, mo, d) => {
    const mi = Number(mo), di = Number(d);
    if (mi < 1 || mi > 12 || di < 1 || di > new Date(Number(y), mi, 0).getDate()) return m;
    return `${MONTHS[mi - 1]} ${di}, ${y}`;
  });
}

// Plain text with **bold** and "- " bullets, rendered as React nodes. The buyer's words
// are free text, so nothing here ever goes through innerHTML.
export function ChatText({ text }) {
  const lines = plainDates(text).split('\n');
  const inline = (s, key) => String(s).split(/(\*\*[^*]+\*\*)/g).map((part, i) => (
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={`${key}-${i}`}>{part.slice(2, -2)}</strong>
      : <React.Fragment key={`${key}-${i}`}>{part}</React.Fragment>
  ));
  return (
    <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--gray-800, #1f2937)' }}>
      {lines.map((l, i) => {
        const t = l.trim();
        if (!t) return <div key={i} style={{ height: 6 }} />;
        const bullet = t.match(/^([-*•]|\d+\.)\s+(.*)$/);
        if (bullet) {
          return (
            <div key={i} style={{ display: 'flex', gap: 6, paddingLeft: 4 }}>
              <span style={{ color: 'var(--orange, #DC582A)' }}>•</span>
              <span>{inline(bullet[2], i)}</span>
            </div>
          );
        }
        const heading = t.match(/^#{1,4}\s+(.*)$/);
        if (heading) return <div key={i} style={{ fontWeight: 700, marginTop: 4 }}>{inline(heading[1], i)}</div>;
        return <div key={i}>{inline(t, i)}</div>;
      })}
    </div>
  );
}

export default function InvoiceContext({ documentId, reloadKey }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [showLines, setShowLines] = useState(false);
  // "That's wrong" on a note, and the vendor's card in a popup. A correction changes what
  // the server says about this bill, so every one of them ends in a re-read (tick).
  const [tick, setTick] = useState(0);
  const [disputing, setDisputing] = useState(null);
  const [disputeText, setDisputeText] = useState('');
  const [busy, setBusy] = useState(false);
  const [actErr, setActErr] = useState('');
  const [card, setCard] = useState(null);

  const act = async (fn) => {
    setBusy(true); setActErr('');
    try { await fn(); setDisputing(null); setDisputeText(''); setTick((t) => t + 1); }
    catch (e) { setActErr(e.message || 'That did not save.'); }
    setBusy(false);
  };
  const openCard = async (key) => {
    setActErr('');
    try { setCard((await apVendorProfiles('get', { key })).vendor); }
    catch (e) { setActErr(e.message || 'Could not open the vendor.'); }
  };

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr('');
    apInvoiceContext(documentId)
      .then((d) => { if (alive) { setData(d); setLoading(false); } })
      .catch((e) => { if (alive) { setErr(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, [documentId, reloadKey, tick]);

  if (loading && !data) {
    return <div style={{ padding: 16, fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>Looking up the history behind this bill…</div>;
  }
  if (err) {
    return (
      <div style={{ padding: 16, fontSize: 13, color: '#b42318' }}>
        Could not load the history for this bill: {err}
      </div>
    );
  }
  if (!data) return null;

  // A C&C bill has no Crab Place history to show, and asking for one is what the
  // checkbox on the form exists to say no to.
  if (data.cnc) {
    return (
      <div style={{ padding: 16, fontSize: 13, color: 'var(--gray-600, #4b5563)', lineHeight: 1.5 }}>
        Marked as Crab &amp; Cruise, so no vendor history is loaded. Crab &amp; Cruise
        runs on its own QuickBooks.
      </div>
    );
  }

  const { document: d, flags = [], vendor: v, suppliers = [], priceMoves = [], recentLines = [],
    alreadyInCpx = [], source, profile } = data;
  const category = d.categoryName || d.category;
  const hasVendor = !!d.vendor;

  return (
    <div style={{ padding: 16 }}>
      {!hasVendor && (
        <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)', marginBottom: 12 }}>
          Pick the vendor and what the bill is for, and the history behind it shows up here.
        </div>
      )}

      {/* What this vendor IS, in a sentence, with this bill placed against it. It comes
          from the vendor's profile, and says so until a person has confirmed that profile. */}
      {profile && profile.text && (
        <div style={{
          border: '1px solid var(--gray-200, #e5e7eb)', borderLeft: `4px solid ${profile.reviewed ? '#1c7c43' : 'var(--orange, #DC582A)'}`,
          borderRadius: 6, padding: '10px 12px', fontSize: 13.5, lineHeight: 1.5, marginBottom: 12,
          display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap',
        }}>
          <div style={{ flex: '1 1 320px' }}>
            {profile.text}
            {profile.notes && profile.notes.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--gray-600, #4b5563)', marginTop: 4 }}>
                {profile.notes.map((n, i) => (
                  <div key={i}>“{n.text}” {n.byName ? n.byName.split(' ')[0] : ''}</div>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{
              fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap',
              background: profile.reviewed ? '#e8f5ec' : '#fff3e6', color: profile.reviewed ? '#1c7c43' : '#9a4a1c',
            }}>
              {profile.reviewed ? `Confirmed${profile.reviewedByName ? ' by ' + profile.reviewedByName.split(' ')[0] : ''}` : 'Not yet confirmed'}
            </span>
            <button className="btn btn-ghost" style={{ padding: 0 }} onClick={() => openCard(profile.key)}>
              Open vendor
            </button>
          </div>
        </div>
      )}

      {flags.length > 0 && (
        <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
          {flags.map((f) => {
            const t = TONES[f.tone] || TONES.info;
            const open = disputing === f.key;
            return (
              <div key={f.key} style={{
                background: t.bg, border: `1px solid ${t.border}`, color: t.fg,
                borderRadius: 6, padding: '9px 12px', fontSize: 13, lineHeight: 1.45,
              }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span style={{ flex: 1 }}>{plainDates(f.text)}</span>
                  {f.canDispute && !open && (
                    <button className="btn btn-ghost" style={{ padding: 0, whiteSpace: 'nowrap' }}
                      onClick={() => { setDisputing(f.key); setDisputeText(''); setActErr(''); }}>
                      That's wrong
                    </button>
                  )}
                </div>
                {/* A name QuickBooks has never seen that begins like one it has. Asked,
                    never assumed: saying yes makes this spelling that vendor from now on. */}
                {f.sameAs && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span>Is this <strong>{f.sameAs.name}</strong>? We have {f.sameAs.bills} bills from them.</span>
                    <button className="btn btn-outline" style={{ fontSize: 12 }} disabled={busy}
                      onClick={() => act(() => apVendorProfiles('same', {
                        vendor: d.rawVendor || d.vendor, asKey: f.sameAs.key, asName: f.sameAs.name,
                      }))}>
                      Yes, Same Vendor
                    </button>
                  </div>
                )}
                {open && (
                  <div style={{ marginTop: 8 }}>
                    <textarea className="form-input" rows={2} autoFocus value={disputeText}
                      onChange={(e) => setDisputeText(e.target.value)}
                      placeholder={f.key === 'large'
                        ? 'Say what a normal bill from them looks like, e.g. their bills run 2,000 to 6,000'
                        : 'Say why in a line, e.g. they are our gas supplier, nobody else sells us propane'}
                      style={{ width: '100%', resize: 'vertical' }} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <button className="btn btn-primary" style={{ fontSize: 12 }} disabled={busy || !disputeText.trim()}
                        onClick={() => act(() => apVendorProfiles('tell', {
                          key: d.vendorKey, name: d.vendor, note: disputeText.trim(),
                          flagKey: f.key, documentId: d.id,
                        }))}>
                        {busy ? 'Saving…' : 'Save'}
                      </button>
                      <button className="btn btn-outline" style={{ fontSize: 12 }} disabled={busy}
                        onClick={() => setDisputing(null)}>Cancel</button>
                      <span style={{ fontSize: 11.5 }}>
                        {f.key === 'large'
                          ? 'Kept on the vendor. To stop this note for good, set their normal range on the vendor card.'
                          : 'Kept on the vendor, and this note stops showing for them.'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {actErr && <div style={{ color: '#b42318', fontSize: 12.5 }}>{actErr}</div>}
        </div>
      )}

      {card && (
        <div className="modal-overlay" onClick={() => { setCard(null); setTick((t) => t + 1); }}>
          <div className="modal" style={{ maxWidth: 760, width: '94%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Vendor</h2>
              <button className="modal-close" onClick={() => { setCard(null); setTick((t) => t + 1); }}>×</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '74vh', overflowY: 'auto' }}>
              <VendorProfileCard vendor={card} />
            </div>
          </div>
        </div>
      )}
      {hasVendor && flags.filter((f) => f.key !== 'no_plan').length === 0 && (
        <div style={{
          background: '#e8f5ec', border: '1px solid #b7e0c6', color: '#1c7c43',
          borderRadius: 6, padding: '9px 12px', fontSize: 13, marginBottom: 14,
          display: 'flex', gap: 8, alignItems: 'center',
        }}>
          <Check size={14} /> Nothing unusual against this vendor's history.
        </div>
      )}

      {hasVendor && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 12 }}>
          <div style={box}>
            <div style={label}><History size={12} /> {d.vendor} over the last two years</div>
            {v && v.bills > 0 ? (
              <>
                <Stat k="Bills in QuickBooks" v={`${v.bills} (${money(v.total)})`} />
                <Stat k="Typical bill from them" v={money(v.medianBill)} />
                <Stat k="This bill" v={money(d.amount)} />
                <Stat k="Usually billed every" v={v.typicalGapDays != null ? `${v.typicalGapDays} days` : '—'} />
                <Stat k="Last bill" v={v.lastBill ? `${fmtDay(v.lastBill.date)}, ${money(v.lastBill.total)}` : '—'} />
                {v.daysSinceLast != null && <Stat k="Days since then" v={v.daysSinceLast} />}
                {v.categories && v.categories.length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--gray-600, #4b5563)' }}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>What they bill us for</div>
                    {v.categories.slice(0, 5).map((c) => (
                      <div key={c.account} style={row}>
                        <span>{c.category}</span>
                        <span>{c.bills} bills · {money(c.spent)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>
                No bills from {d.vendor} in QuickBooks in the last two years.
              </div>
            )}
            {alreadyInCpx.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--gray-600, #4b5563)' }}>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>Other invoices from them in CPx</div>
                {alreadyInCpx.slice(0, 4).map((x) => (
                  <div key={x.id} style={row}>
                    <span>#{x.id} · {x.invoice || 'no number'} · {fmtDay(x.date)}</span>
                    <span>{money(x.amount)} · {x.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={box}>
            <div style={label}><Users size={12} /> {category ? `Who we buy ${category} from` : 'What this is for'}</div>
            {!category ? (
              <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>
                Pick what the bill is for and this shows who we normally buy it from.
              </div>
            ) : suppliers.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--gray-500, #6b7280)' }}>
                No {category} bills in QuickBooks in the last two years.
              </div>
            ) : (
              <>
              {data.categoryTypicalBill != null && (
                <div style={{ fontSize: 12, color: 'var(--gray-600, #4b5563)', marginBottom: 6 }}>
                  A typical {category} bill is about {money(data.categoryTypicalBill)}.
                </div>
              )}
              {suppliers.slice(0, 6).map((s) => (
                <div key={s.vendor} style={{
                  ...row,
                  fontWeight: s.isThisVendor ? 700 : 400,
                  background: s.isThisVendor ? '#fff7ed' : 'transparent',
                  borderRadius: 4, padding: '3px 6px', margin: '0 -6px',
                }}>
                  <span>{s.vendor}{s.isThisVendor ? ' (this bill)' : ''}</span>
                  <span>{s.bills} bills · last {fmtDay(s.lastBill)}</span>
                </div>
              ))}
              </>
            )}
          </div>
        </div>
      )}

      {priceMoves.length > 0 && (
        <div style={{ ...box, marginBottom: 12 }}>
          <div style={label}><TrendingUp size={12} /> Price changes from {d.vendor}</div>
          {priceMoves.map((p, i) => (
            <div key={i} style={row}>
              <span>{p.item}</span>
              <span style={{ color: p.pct > 0 ? '#9a4a1c' : '#1c7c43', fontWeight: 600 }}>
                {money(p.previous.price)} to {money(p.latest.price)} per {p.unit}
                {' '}({p.pct > 0 ? '+' : ''}{p.pct}%, {fmtDay(p.latest.date)})
              </span>
            </div>
          ))}
        </div>
      )}

      {recentLines.length > 0 && (
        <div style={{ ...box, marginBottom: 12 }}>
          <button className="btn btn-ghost" style={{ padding: 0, fontSize: 12.5 }}
            onClick={() => setShowLines((x) => !x)}>
            {showLines ? 'Hide' : 'Show'} the last {recentLines.length} lines we paid {d.vendor}
            {category ? ` for ${category}` : ''}
          </button>
          {showLines && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, marginTop: 8 }}>
              <tbody>
                {recentLines.map((l, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--gray-100, #f3f4f6)' }}>
                    <td style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>{fmtDay(l.date)}</td>
                    <td style={{ padding: '5px 6px' }}>{l.description}</td>
                    <td style={{ padding: '5px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{money(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* The buyer's plan itself lives in the dedicated "The buyer's plan" card below
          this one, which is the interactive coach. Mercedes, 2026-09-18: showing a
          passive copy up here on top of that card was redundant, and it added a second
          section with the same heading on one screen. */}

      {/* ⚠️ TWO FACTS, SAID SEPARATELY. This used to read "QuickBooks bills are in
          through {date}" off the newest bill's own date, which is not how fresh the
          mirror is. It read 2026-09-09 for nine days while the sync was only ever run
          by hand, and then read 2026-10-01 off one post-dated rent bill. What somebody
          needs from this line is when we last looked. */}
      {source && (
        <div style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}>
          {source.note}
          {source.mirror && (
            <>
              {' '}QuickBooks was last read {agoText(source.mirror.ageHours)}
              {source.mirror.newestPostedBill
                ? `, and the newest bill in it is dated ${fmtDay(source.mirror.newestPostedBill)}`
                : ''}.
              {source.mirror.ageHours != null && source.mirror.ageHours > 6 && (
                <span style={{ color: 'var(--orange, #DC582A)' }}>
                  {' '}That is older than it should be, so the last day or two of bills may be missing.
                </span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
